import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { BottomSheet, scrollFieldIntoView, sheetInput } from "./BottomSheet";
import { CopyNameButton } from "./CopyNameButton";
import { changedFields, newRequestId } from "../lib/doorGuests";

const Field = ({ label, children }) => (
  <label className="block space-y-1.5">
    <span className="text-xs font-bold uppercase tracking-[0.15em] text-slate-500">{label}</span>
    {children}
  </label>
);

export const NameSheet = ({ guest, onClose, onSave }) => {
  const [form, setForm] = useState({ display_name: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  useEffect(() => {
    if (guest) setForm({ display_name: guest.display_name ?? "", first_name: guest.first_name ?? "", last_name: guest.last_name ?? "" });
    setError("");
  }, [guest]);
  const submit = async (e) => {
    e.preventDefault();
    const changes = changedFields(guest, form);
    if (!Object.keys(changes).length) return onClose();
    setBusy(true);
    setError(await onSave(guest, changes));
    setBusy(false);
  };
  return (
    <BottomSheet open={!!guest} onClose={onClose} title={guest ? `Name for ${guest.identifier}` : ""} testid="door-name-sheet">
      <form onSubmit={submit} className="space-y-4">
        <div className="space-y-1">
          <Field label="Display name">
            <input data-testid="door-display-name-input" className={sheetInput} autoComplete="off" maxLength={200} value={form.display_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
          </Field>
          <CopyNameButton key={guest?.id} testid="door-copy-display-name" display={form.display_name} first={form.first_name} onCopy={(v) => setForm((f) => ({ ...f, first_name: v }))} />
        </div>
        <Field label="First name">
          <input data-testid="door-first-name-input" className={sheetInput} autoComplete="off" maxLength={80} value={form.first_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
        </Field>
        <Field label="Last name">
          <input data-testid="door-last-name-input" className={sheetInput} autoComplete="off" maxLength={80} value={form.last_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
        </Field>
        {error && <p data-testid="door-name-error" role="alert" className="text-sm font-semibold text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" data-testid="door-name-cancel" onClick={onClose} className="min-h-[48px] flex-1 rounded-xl border border-slate-200 text-base font-semibold text-slate-700">Cancel</button>
          <button type="submit" data-testid="door-name-save" disabled={busy} className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-blue-700 text-base font-bold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Save"}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
};

export const AddGuestSheet = ({ open, prefill, label, onClose, onAdd }) => {
  const [form, setForm] = useState({ identifier: "", display_name: "", first_name: "", last_name: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const requestId = useRef(newRequestId());
  useEffect(() => {
    if (!open) return;
    setForm({ identifier: prefill || "", display_name: "", first_name: "", last_name: "" });
    setError("");
    requestId.current = newRequestId();
  }, [open, prefill]);
  const submit = async (e) => {
    e.preventDefault();
    if (!form.identifier.trim()) return setError(`${label} is required`);
    setBusy(true);
    const err = await onAdd({ ...form, request_id: requestId.current });
    setBusy(false);
    setError(err);
  };
  return (
    <BottomSheet open={open} onClose={onClose} title="Add guest" testid="door-add-sheet">
      <form onSubmit={submit} noValidate className="space-y-4">
        <Field label={`${label} *`}>
          <input data-testid="door-add-identifier-input" className={sheetInput} autoComplete="off" maxLength={200} value={form.identifier} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, identifier: e.target.value })} />
        </Field>
        <Field label="Display name">
          <input data-testid="door-add-display-input" className={sheetInput} autoComplete="off" maxLength={200} value={form.display_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, display_name: e.target.value })} />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="First name">
            <input data-testid="door-add-first-input" className={sheetInput} autoComplete="off" maxLength={80} value={form.first_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, first_name: e.target.value })} />
          </Field>
          <Field label="Last name">
            <input data-testid="door-add-last-input" className={sheetInput} autoComplete="off" maxLength={80} value={form.last_name} onFocus={scrollFieldIntoView} onChange={(e) => setForm({ ...form, last_name: e.target.value })} />
          </Field>
        </div>
        <p className="text-xs text-slate-500">The guest is added to the list. They are not checked in until you tap Check in.</p>
        {error && <p data-testid="door-add-error" role="alert" className="text-sm font-semibold text-red-600">{error}</p>}
        <div className="flex gap-3 pt-1">
          <button type="button" data-testid="door-add-cancel" onClick={onClose} className="min-h-[48px] flex-1 rounded-xl border border-slate-200 text-base font-semibold text-slate-700">Cancel</button>
          <button type="submit" data-testid="door-add-save" disabled={busy} className="inline-flex min-h-[48px] flex-1 items-center justify-center rounded-xl bg-blue-700 text-base font-bold text-white disabled:opacity-60">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" /> : "Add guest"}
          </button>
        </div>
      </form>
    </BottomSheet>
  );
};
